import React, { Dispatch, SetStateAction, useState, useCallback, useRef, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { AppService } from '../../models/app.service.model';
import { Contact } from '../../models/contact.model';
import { legendValid } from '../../utils/legend.data.util';
import { formatContact } from '../../scripts/format.value.script';
import { modalShow } from '../../utils/modal.util';
import { useDropdownKeyboardNav } from '../../hooks/useDropdownKeyboardNav';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';

import AppLegend from './Legend';
import AppModalContactCreate from '../modal/ModalContactCreate';

export interface AppInputContactProps {
  appServiceForUser: AppService,
  companyForUser: Company,
  settingsForCompany?: any,
  contact: {value: string, valid: boolean, object: any},
  setContact: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setLanguage?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrTtype?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  loadContactsForCompany: Function
};

const formatContactWithCode = (contact: any): string => {
  const base = formatContact(contact)
  const code = (contact?.data?.contact_code ?? '').trim()
  return code.length > 0 ? `${base} (${code})` : base
}

const AppInputContact: React.FunctionComponent<AppInputContactProps> = ({appServiceForUser, companyForUser, settingsForCompany, contact, setContact, setLanguage, setQrTtype, loadContactsForCompany}) => {
  const {lang} = StateLanguage()

  const [searchText, setSearchText] = useState('')
  const [results, setResults] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Server search with debounce + AbortController
  useEffect(() => {
    if (!isOpen) return
    if (searchText.trim().length < 2) { setResults([]); setLoading(false); return }

    setLoading(true)
    const timer = setTimeout(() => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      ApiDigitalAdminContact.getContacts(companyForUser.id, searchText.trim(), '', '', '', 1, 50, ctrl.signal)
        .then(res => {
          if (res.status === 200 && res.data) {
            const items: Contact[] = res.data?.items ?? res.data?.contacts ?? (Array.isArray(res.data) ? res.data : [])
            setResults(items)
          } else {
            setResults([])
          }
        })
        .catch(() => { /* cancelled or error — ignore */ })
        .finally(() => setLoading(false))
    }, 300)

    return () => clearTimeout(timer)
  }, [searchText, companyForUser.id, isOpen])

  // Cleanup abort on unmount
  useEffect(() => {
    return () => { abortRef.current?.abort() }
  }, [])

  const handleChangeSearch = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleChangeContact = useCallback((item: Contact) => {
    setContact({value: item.id, valid: true, object: item})
    legendValid('legend-customer-required')

    if (setLanguage) {
      setLanguage({value: item.data.language, valid: true})
      legendValid('legend-language-required')
    }

    if (setQrTtype) {
      setQrTtype({value: item.data.qr_type, valid: true})
      legendValid('legend-qr-type-required')
    }
  }, [setContact, setLanguage, setQrTtype])

  const executeShowModalContactCreate = () => {
    modalShow('modal-contact-create')
  }

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow)
    if (nextShow) {
      setSearchText('')
      setResults([])
    }
  }

  const handleClose = useCallback(() => {
    toggleRef.current?.click()
  }, [])

  const { highlightedIndex, handleKeyDown, getOptionId, activeDescendant, listboxId } =
    useDropdownKeyboardNav({
      itemCount: results.length,
      onSelect: (idx) => { handleChangeContact(results[idx]); handleClose(); },
      isOpen,
      onClose: handleClose,
      idPrefix: 'contact',
    })

  const renderDropdownContent = () => {
    if (searchText.trim().length < 2) {
      return (
        <li className="select2-results__option select2-results__message" role="alert">{lang.labels.searchContact}</li>
      )
    }
    if (loading) {
      return (
        <li className="select2-results__option select2-results__message" role="alert">...</li>
      )
    }
    if (results.length === 0) {
      return (
        <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
      )
    }
    return (
      <>
        { results.map((item, index) => { return (
          <li key={index} id={getOptionId(index)} className={`select2-results__option select2-results__option--selectable ${item.id === contact.value ? "select2-results__option--selected" : ""} ${index === highlightedIndex ? "select2-results__option--highlighted" : ""}`} role="option" aria-selected={item.id === contact.value}>
            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => { handleChangeContact(item); handleClose(); }}>{formatContactWithCode(item)}</Dropdown.Item>
          </li>
        )})}
      </>
    )
  }

  return (
    <>
      <div className="d-flex align-self-center">
        <div className="flex-grow-1 me-2">
          <Dropdown onToggle={handleToggle}>
            <Dropdown.Toggle ref={toggleRef} variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
              <span className="selection">
                <span className="select2-selection select2-selection--single form-select">
                  <span className="select2-selection__rendered" role="textbox">
                    {contact.valid ? formatContactWithCode(contact.object) : lang.labels.selectOption}
                  </span>
                </span>
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
              <span className="select2-dropdown select2-dropdown--below">
                <span className="select2-search select2-search--dropdown">
                  <input className="select2-search__field" type="text" autoComplete="off" name="contact" role="combobox" aria-autocomplete="list" aria-controls={listboxId} aria-activedescendant={activeDescendant} aria-expanded={isOpen} value={searchText} onChange={handleChangeSearch} onKeyDown={handleKeyDown} />
                </span>
                <span className="select2-results">
                  <ul className="select2-results__options" id={listboxId} role="listbox">
                    {renderDropdownContent()}
                  </ul>
                </span>
              </span>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="flex-shrink-0">
          <button className="btn btn-sm btn-light-primary h-100" type="button" onClick={executeShowModalContactCreate}>
            <div className="d-flex align-items-center">
              <span className="svg-icon svg-icon-3 ms-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                  <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                  <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                </svg>
              </span>
              {lang.labels.addContact}
            </div>
          </button>
        </div>
      </div>
      <div className="form-text text-justify">{lang.labels.searchSelectContact}</div>
      <AppLegend attribute={{check: contact.valid, name: "contact", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
      <AppModalContactCreate appServiceForUser={appServiceForUser} companyForUser={companyForUser} settingsForCompany={settingsForCompany} setContact={setContact} setLanguageDocument={setLanguage} setQrTtypeDocument={setQrTtype} loadContactsForCompany={loadContactsForCompany}></AppModalContactCreate>
    </>
  )
}

export default AppInputContact;